class ApostleStorefront {
  constructor() {
    this.revealElements = [];
    this.toastTimer = null;
    this.revealObserver = null;
    this.drawerStateObserver = null;
    this.observedPanels = new WeakSet();

    this.overlay = document.getElementById("ApostleOverlay");
    this.mobileMenu = document.querySelector("[data-mobile-menu]");
    this.searchDrawer = document.querySelector("[data-search-drawer]");
    this.searchInput = document.querySelector("[data-search-input]");

    this.openSearch = () => {};
    this.closeSearch = () => {};

    this.init();
  }

  init() {
    this.initializeRevealAnimations();
    this.initializeHeader();
    this.initializeMobileMenu();
    this.initializeSearchDrawer();
    this.initializeEscapeKey();
    this.initializeOverlay();
    this.initializeGlobalEvents();
    this.initializeDrawerStateObserver();
    this.initializeWishlist();
    this.initializeQuickAdd();
    this.initializeBackToTop();
    this.initializeShopifyThemeEditor();
  }

  initializeHeader() {
    const header = document.querySelector("[data-header]");

    if (!header || header.dataset.apostleInitialized === "true") {
      return;
    }

    header.dataset.apostleInitialized = "true";

    const updateHeader = () => {
      header.classList.toggle("is-scrolled", window.scrollY > 16);
    };

    updateHeader();

    window.addEventListener("scroll", updateHeader, {
      passive: true,
    });
  }

  initializeMobileMenu() {
    this.mobileMenu = document.querySelector("[data-mobile-menu]");

    const openButton = document.querySelector(
      "[data-mobile-menu-open]"
    );

    const closeButton = document.querySelector(
      "[data-mobile-menu-close]"
    );

    if (
      !this.mobileMenu ||
      !openButton ||
      openButton.dataset.apostleInitialized === "true"
    ) {
      return;
    }

    openButton.dataset.apostleInitialized = "true";

    const openMenu = () => {
      this.closeSearch();

      this.mobileMenu.classList.add("is-open");
      this.mobileMenu.setAttribute("aria-hidden", "false");
      openButton.setAttribute("aria-expanded", "true");

      document.body.classList.add("menu-open");
      this.openOverlay();

      window.setTimeout(() => {
        closeButton?.focus();
      }, 250);
    };

    const closeMenu = () => {
      this.mobileMenu.classList.remove("is-open");
      this.mobileMenu.setAttribute("aria-hidden", "true");
      openButton.setAttribute("aria-expanded", "false");

      document.body.classList.remove("menu-open");

      if (!this.hasOpenDrawer()) {
        this.closeOverlay();
      }
    };

    this.closeMobileMenu = closeMenu;

    openButton.addEventListener("click", openMenu);

    if (
      closeButton &&
      closeButton.dataset.apostleInitialized !== "true"
    ) {
      closeButton.dataset.apostleInitialized = "true";
      closeButton.addEventListener("click", closeMenu);
    }

    this.mobileMenu.querySelectorAll("a").forEach((link) => {
      if (link.dataset.apostleInitialized === "true") {
        return;
      }

      link.dataset.apostleInitialized = "true";
      link.addEventListener("click", closeMenu);
    });

    document.addEventListener(
      "apostle:close-drawers",
      closeMenu
    );
  }

  initializeSearchDrawer() {
    this.searchDrawer = document.querySelector(
      "[data-search-drawer]"
    );

    this.searchInput = document.querySelector(
      "[data-search-input]"
    );

    const openButtons = document.querySelectorAll(
      "[data-search-open]"
    );

    const closeButton = document.querySelector(
      "[data-search-close]"
    );

    if (!this.searchDrawer || !openButtons.length) {
      return;
    }

    const openSearch = () => {
      this.closeMobileMenu();

      this.searchDrawer.classList.add("is-open");
      this.searchDrawer.setAttribute("aria-hidden", "false");

      document.body.classList.add("drawer-open");
      this.openOverlay();

      window.setTimeout(() => {
        this.searchInput?.focus();
      }, 350);
    };

    const closeSearch = () => {
      this.searchDrawer.classList.remove("is-open");
      this.searchDrawer.setAttribute("aria-hidden", "true");

      this.updateDrawerBodyState();

      if (!this.hasOpenDrawer()) {
        this.closeOverlay();
      }
    };

    this.openSearch = openSearch;
    this.closeSearch = closeSearch;

    openButtons.forEach((button) => {
      if (button.dataset.apostleInitialized === "true") {
        return;
      }

      button.dataset.apostleInitialized = "true";
      button.addEventListener("click", openSearch);
    });

    if (
      closeButton &&
      closeButton.dataset.apostleInitialized !== "true"
    ) {
      closeButton.dataset.apostleInitialized = "true";
      closeButton.addEventListener("click", closeSearch);
    }

    if (
      this.searchDrawer.dataset.apostleInitialized !== "true"
    ) {
      this.searchDrawer.dataset.apostleInitialized = "true";

      document.addEventListener(
        "apostle:close-drawers",
        closeSearch
      );
    }
  }

  closeMobileMenu() {
    const menuButton = document.querySelector(
      "[data-mobile-menu-open]"
    );

    this.mobileMenu?.classList.remove("is-open");
    this.mobileMenu?.setAttribute("aria-hidden", "true");

    menuButton?.setAttribute("aria-expanded", "false");
    document.body.classList.remove("menu-open");
  }

  hasOpenDrawer() {
    return [
      this.searchDrawer ||
        document.querySelector("[data-search-drawer]"),
      document.querySelector("[data-cart-drawer]"),
      document.querySelector("[data-wishlist-drawer]"),
    ].some(
      (drawer) =>
        drawer?.classList.contains("is-open") &&
        drawer.getAttribute("aria-hidden") !== "true"
    );
  }

  updateDrawerBodyState() {
    document.body.classList.toggle(
      "drawer-open",
      this.hasOpenDrawer()
    );
  }

  isMobileMenuOpen() {
    const menu =
      this.mobileMenu ||
      document.querySelector("[data-mobile-menu]");

    return Boolean(
      menu?.classList.contains("is-open") &&
        menu.getAttribute("aria-hidden") !== "true"
    );
  }

  syncOverlayState() {
    this.overlay = document.getElementById("ApostleOverlay");

    const menuOpen = this.isMobileMenuOpen();
    const drawerOpen = this.hasOpenDrawer();
    const shouldShowOverlay = menuOpen || drawerOpen;

    document.body.classList.toggle("menu-open", menuOpen);
    document.body.classList.toggle("drawer-open", drawerOpen);

    if (!this.overlay) {
      return;
    }

    this.overlay.classList.toggle(
      "is-active",
      shouldShowOverlay
    );

    this.overlay.setAttribute(
      "aria-hidden",
      String(!shouldShowOverlay)
    );
  }

  initializeDrawerStateObserver(scope = document) {
    if (!("MutationObserver" in window)) {
      this.syncOverlayState();
      return;
    }

    if (!this.drawerStateObserver) {
      this.drawerStateObserver = new MutationObserver(() => {
        window.requestAnimationFrame(() => {
          this.syncOverlayState();
        });
      });
    }

    const panelSelector = [
      "[data-mobile-menu]",
      "[data-search-drawer]",
      "[data-cart-drawer]",
      "[data-wishlist-drawer]",
    ].join(",");

    const panels = [
      ...(scope.matches?.(panelSelector) ? [scope] : []),
      ...scope.querySelectorAll(panelSelector),
    ];

    panels.forEach((panel) => {
      if (this.observedPanels.has(panel)) {
        return;
      }

      this.observedPanels.add(panel);

      this.drawerStateObserver.observe(panel, {
        attributes: true,
        attributeFilter: ["class", "aria-hidden"],
      });
    });

    this.syncOverlayState();
  }

  initializeRevealAnimations(scope = document) {
    const newRevealElements = Array.from(
      scope.querySelectorAll(
        "[data-reveal]:not([data-apostle-reveal-initialized])"
      )
    );

    if (!newRevealElements.length) {
      return;
    }

    newRevealElements.forEach((element) => {
      element.dataset.apostleRevealInitialized = "true";
    });

    this.revealElements.push(...newRevealElements);

    if (
      window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches ||
      !("IntersectionObserver" in window)
    ) {
      newRevealElements.forEach((element) => {
        element.classList.add("is-visible");
      });

      return;
    }

    if (!this.revealObserver) {
      this.revealObserver = new IntersectionObserver(
        (entries, currentObserver) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }

            entry.target.classList.add("is-visible");
            currentObserver.unobserve(entry.target);
          });
        },
        {
          root: null,
          rootMargin: "0px 0px -8% 0px",
          threshold: 0.12,
        }
      );
    }

    newRevealElements.forEach((element) => {
      this.revealObserver.observe(element);
    });
  }

  initializeEscapeKey() {
    if (
      document.documentElement.dataset
        .apostleEscapeInitialized
    ) {
      return;
    }

    document.documentElement.dataset
      .apostleEscapeInitialized = "true";

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") {
        return;
      }

      document.dispatchEvent(
        new CustomEvent("apostle:close-drawers")
      );
    });
  }

  initializeOverlay() {
    this.overlay = document.getElementById("ApostleOverlay");

    if (
      !this.overlay ||
      this.overlay.dataset.apostleInitialized === "true"
    ) {
      return;
    }

    this.overlay.dataset.apostleInitialized = "true";

    this.overlay.addEventListener("click", () => {
      document.dispatchEvent(
        new CustomEvent("apostle:close-drawers")
      );
    });
  }

  openOverlay() {
    if (!this.overlay) {
      return;
    }

    this.overlay.classList.add("is-active");
    this.overlay.setAttribute("aria-hidden", "false");
  }

  closeOverlay() {
    if (!this.overlay) {
      return;
    }

    this.overlay.classList.remove("is-active");
    this.overlay.setAttribute("aria-hidden", "true");
  }

  initializeGlobalEvents() {
    if (
      document.documentElement.dataset
        .apostleGlobalEventsInitialized
    ) {
      return;
    }

    document.documentElement.dataset
      .apostleGlobalEventsInitialized = "true";

    document.addEventListener("apostle:toast", (event) => {
      const message =
        event.detail?.message || "Updated successfully.";

      this.showToast(message);
    });

    document.addEventListener(
      "apostle:drawer-opened",
      () => {
        this.closeMobileMenu();
        this.closeSearch();

        window.requestAnimationFrame(() => {
          this.initializeDrawerStateObserver();
          this.syncOverlayState();
        });
      }
    );

    document.addEventListener(
      "apostle:drawer-closed",
      () => {
        window.requestAnimationFrame(() => {
          this.syncOverlayState();
        });
      }
    );
  }

  initializeWishlist() {
    const storageKey = "apostle-wishlist";

    let wishlist = this.getWishlist(storageKey);

    const updateCount = () => {
      document
        .querySelectorAll("[data-wishlist-count]")
        .forEach((element) => {
          element.textContent = wishlist.length;
          element.hidden = wishlist.length === 0;
        });
    };

    const updateButtons = () => {
      document
        .querySelectorAll(
          "[data-wishlist-button][data-product-handle]"
        )
        .forEach((button) => {
          const handle = button.dataset.productHandle;

          const isActive = wishlist.some(
            (item) => item.handle === handle
          );

          button.classList.toggle("is-active", isActive);

          button.setAttribute(
            "aria-pressed",
            String(isActive)
          );

          button.setAttribute(
            "aria-label",
            isActive
              ? `Remove ${
                  button.dataset.productTitle || "product"
                } from wishlist`
              : `Add ${
                  button.dataset.productTitle || "product"
                } to wishlist`
          );
        });
    };

    const refreshWishlistUI = () => {
      wishlist = this.getWishlist(storageKey);
      updateCount();
      updateButtons();
    };

    if (
      !document.documentElement.dataset
        .apostleWishlistInitialized
    ) {
      document.documentElement.dataset
        .apostleWishlistInitialized = "true";

      document.addEventListener("click", (event) => {
        const button = event.target.closest(
          "[data-wishlist-button][data-product-handle]"
        );

        if (!button) {
          return;
        }

        event.preventDefault();

        wishlist = this.getWishlist(storageKey);

        const product = {
          handle: button.dataset.productHandle,
          title: button.dataset.productTitle,
          url: button.dataset.productUrl,
          image: button.dataset.productImage,
        };

        const existingIndex = wishlist.findIndex(
          (item) => item.handle === product.handle
        );

        if (existingIndex >= 0) {
          wishlist.splice(existingIndex, 1);
          this.showToast("Removed from your wishlist.");
        } else {
          wishlist.push(product);
          this.showToast("Added to your wishlist.");
        }

        try {
          window.localStorage.setItem(
            storageKey,
            JSON.stringify(wishlist)
          );
        } catch (error) {
          console.warn(
            "APOSTLE: Wishlist could not be saved.",
            error
          );
        }

        updateCount();
        updateButtons();

        document.dispatchEvent(
          new CustomEvent("apostle:wishlist-updated", {
            detail: {
              wishlist,
            },
          })
        );
      });

      window.addEventListener("storage", (event) => {
        if (event.key !== storageKey) {
          return;
        }

        refreshWishlistUI();
      });

      document.addEventListener(
        "apostle:wishlist-refresh",
        refreshWishlistUI
      );
    }

    refreshWishlistUI();
  }

  getWishlist(storageKey = "apostle-wishlist") {
    try {
      const storedWishlist = JSON.parse(
        window.localStorage.getItem(storageKey) || "[]"
      );

      return Array.isArray(storedWishlist)
        ? storedWishlist
        : [];
    } catch (error) {
      return [];
    }
  }

  initializeQuickAdd(scope = document) {
    const forms = scope.querySelectorAll(
      "[data-quick-add-form]:not([data-apostle-initialized])"
    );

    forms.forEach((form) => {
      form.dataset.apostleInitialized = "true";

      form.addEventListener("submit", async (event) => {
        event.preventDefault();

        const button = form.querySelector(
          'button[type="submit"], button:not([type])'
        );

        const label = form.querySelector(
          "[data-quick-add-label]"
        );

        if (!button || !label) {
          return;
        }

        const originalLabel = label.textContent;

        button.disabled = true;
        button.setAttribute("aria-busy", "true");
        label.textContent = "Adding";

        try {
          const root = window.Shopify?.routes?.root || "/";

          const response = await fetch(
            `${root}cart/add.js`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
              },
              body: new FormData(form),
            }
          );

          if (!response.ok) {
            const errorData = await response
              .json()
              .catch(() => null);

            throw new Error(
              errorData?.description ||
                errorData?.message ||
                "Unable to add item"
            );
          }

          const addedItem = await response.json();

          const cartResponse = await fetch(
            `${root}cart.js`,
            {
              headers: {
                Accept: "application/json",
              },
            }
          );

          if (!cartResponse.ok) {
            throw new Error("Unable to refresh cart");
          }

          const cart = await cartResponse.json();

          this.updateCartCounts(cart.item_count);

          label.textContent = "Added";
          this.showToast("Added to your shopping bag.");

          document.dispatchEvent(
            new CustomEvent("apostle:cart-updated", {
              detail: {
                cart,
                addedItem,
                source: "quick-add",
              },
            })
          );

          window.setTimeout(() => {
            label.textContent = originalLabel;
          }, 1600);
        } catch (error) {
          console.error(
            "APOSTLE quick add error:",
            error
          );

          label.textContent = "Try again";

          this.showToast(
            error.message ||
              "This item could not be added. Please try again."
          );

          window.setTimeout(() => {
            label.textContent = originalLabel;
          }, 1800);
        } finally {
          button.disabled = false;
          button.removeAttribute("aria-busy");
        }
      });
    });
  }

  updateCartCounts(itemCount) {
    document
      .querySelectorAll("[data-cart-count]")
      .forEach((element) => {
        element.textContent = itemCount;
        element.hidden = itemCount === 0;
      });

    document
      .querySelectorAll("[data-cart-drawer-open]")
      .forEach((button) => {
        button.setAttribute(
          "aria-label",
          `View shopping bag with ${itemCount} ${
            itemCount === 1 ? "item" : "items"
          }`
        );
      });
  }

  initializeBackToTop(scope = document) {
    const buttons = scope.querySelectorAll(
      "[data-apostle-back-to-top]"
    );

    buttons.forEach((button) => {
      if (button.dataset.apostleInitialized === "true") {
        return;
      }

      button.dataset.apostleInitialized = "true";

      button.addEventListener("click", () => {
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia(
            "(prefers-reduced-motion: reduce)"
          ).matches
            ? "auto"
            : "smooth",
        });
      });
    });
  }

  initializeShopifyThemeEditor() {
    if (
      document.documentElement.dataset
        .apostleThemeEditorInitialized
    ) {
      return;
    }

    document.documentElement.dataset
      .apostleThemeEditorInitialized = "true";

    document.addEventListener(
      "shopify:section:load",
      (event) => {
        const section = event.target || document;

        this.overlay =
          document.getElementById("ApostleOverlay");

        this.mobileMenu = document.querySelector(
          "[data-mobile-menu]"
        );

        this.searchDrawer = document.querySelector(
          "[data-search-drawer]"
        );

        this.searchInput = document.querySelector(
          "[data-search-input]"
        );

        this.initializeRevealAnimations(section);
        this.initializeHeader();
        this.initializeMobileMenu();
        this.initializeSearchDrawer();
        this.initializeOverlay();
        this.initializeDrawerStateObserver(section);
        this.initializeWishlist();
        this.initializeQuickAdd(section);
        this.initializeBackToTop(section);
        this.syncOverlayState();
      }
    );

    document.addEventListener(
      "shopify:section:unload",
      () => {
        window.requestAnimationFrame(() => {
          this.syncOverlayState();
        });
      }
    );
  }

  showToast(message) {
    const toast = document.getElementById("ApostleToast");

    const messageElement = toast?.querySelector(
      "[data-toast-message]"
    );

    if (!toast || !messageElement) {
      return;
    }

    window.clearTimeout(this.toastTimer);

    messageElement.textContent = message;
    toast.classList.add("is-visible");

    this.toastTimer = window.setTimeout(() => {
      toast.classList.remove("is-visible");
    }, 3200);
  }
}

const initializeApostleStorefront = () => {
  if (window.Apostle instanceof ApostleStorefront) {
    return;
  }

  window.Apostle = new ApostleStorefront();
};

if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    initializeApostleStorefront,
    {
      once: true,
    }
  );
} else {
  initializeApostleStorefront();
}